import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import i18n from "@/lib/i18n"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Applique une opacité à une couleur (hex ou CSS variable).
 * - Hex : concatène l'opacité hex (#34DCA715)
 * - CSS var : utilise color-mix()
 * @param color - Couleur hex ou var(--xxx)
 * @param opacityHex - Opacité hex sur 2 caractères (ex: '15', '08', '20')
 */
export function colorWithAlpha(color: string, opacityHex: string): string {
  if (!color || color.startsWith('var(')) {
    const pct = Math.round((parseInt(opacityHex, 16) / 255) * 100);
    return `color-mix(in srgb, ${color || 'var(--secondary)'} ${pct}%, transparent)`;
  }
  return `${color}${opacityHex}`;
}
/**
 * Couleur du badge par rôle. Les libellés, eux, vivent dans les fichiers de
 * langue (common.json > role) : ici on ne garde que ce qui relève du design.
 */
const ROLE_BADGE_CLASS: Record<string, string> = {
  admin: 'bg-foreground text-background',
  owner: 'bg-foreground text-background',
  head_department: 'bg-brand/15 text-brand',
  employee: 'bg-muted text-muted-foreground',
  user: 'bg-muted text-muted-foreground',
  guest: 'bg-muted text-muted-foreground',
};

const normalizeRole = (str: string): string =>
  str
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z_]/g, '') || '';

export function getRoleLabel(role: string): string {
  const key = normalizeRole(role);
  // Rôle inconnu : on affiche la valeur brute plutôt qu'une clé de traduction.
  return i18n.exists(`common:role.${key}`) ? i18n.t(`common:role.${key}`) : role;
}

export function getRoleBadgeClass(role: string): string {
  return ROLE_BADGE_CLASS[normalizeRole(role)] ?? 'bg-muted text-muted-foreground';
}

/** Couleur du badge par statut, indexée sur le code stable AccountStatusEnum. */
const USER_STATUS_BADGE_CLASS: Record<string, string> = {
  ARCHIVED: 'bg-muted text-muted-foreground',
  BLOCKED: 'bg-destructive text-destructive-foreground',
  INACTIVE: 'bg-muted text-muted-foreground',
  EMAIL_VERIFIED: 'bg-success text-success-foreground',
  ACTIVE: 'bg-success text-success-foreground',
};

/**
 * Libellés français historiques renvoyés par l'API sous la clé `status`.
 *
 * Ce champ est figé : l'application mobile s'appuie dessus, il reste donc en
 * français quelle que soit la langue demandée (cf. UserService::getAccountStatus
 * côté API). On le remappe ici vers le code stable pour pouvoir malgré tout
 * afficher un statut traduit.
 *
 * Les clients devraient préférer `status_code`, ajouté à UserResource ; cette
 * table reste le filet de sécurité pour les réponses qui ne le portent pas.
 */
const LEGACY_FR_LABEL_TO_CODE: Record<string, string> = {
  'Compte archivé': 'ARCHIVED',
  'Compte suspendu': 'BLOCKED',
  'Inactif': 'INACTIVE',
  'Adresse mail vérifiée': 'EMAIL_VERIFIED',
  'Compte actif': 'ACTIVE',
};

/**
 * Ramène au code stable une valeur de statut, quelle que soit sa forme :
 * code (`ACTIVE`, `active`) ou libellé français hérité (`Compte actif`).
 */
function toStatusCode(status: string): string | undefined {
  if (!status) return undefined;

  const upper = status.toUpperCase();
  if (upper in USER_STATUS_BADGE_CLASS) return upper;

  return LEGACY_FR_LABEL_TO_CODE[status];
}

export function getUserStatusLabel(status: string): string {
  const code = toStatusCode(status);
  // Statut inconnu : on affiche la valeur brute plutôt qu'une clé de traduction.
  return code ? i18n.t(`common:userStatus.${code}`) : status;
}

export function getUserStatusBadgeClass(status: string): string {
  const code = toStatusCode(status);
  return (code && USER_STATUS_BADGE_CLASS[code]) || 'bg-muted text-muted-foreground';
}

/**
 * Normalise une chaîne de caractères pour l'API backend
 * - Normalise les caractères Unicode (NFC) : gère les accents décomposés (Mac)
 * - Remplace les guillemets typographiques par des guillemets simples/doubles standards
 * - Supprime les caractères de contrôle invisibles
 */
export function sanitizeText(text: string): string {
  if (!text) return text;
  
  return text
    // Normaliser en NFC (forme composée) - gère les accents décomposés de Mac
    .normalize('NFC')
    // Remplacer les guillemets typographiques par des guillemets standards
    .replace(/['']/g, "'")
    .replace(/[""]/g, '"')
    // Supprimer les caractères de contrôle invisibles (sauf newlines et tabs)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .trim();
}